import cn from "classnames";
import {
	type ComponentPropsWithRef,
	type FC,
	type ReactNode,
	Suspense,
	useEffect,
	useState,
} from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { CompanyUserRole } from "shared";
import defaultLogo from "../assets/moxie-horizontal-color.png";
import { useAuth } from "../auth/useAuth";
import { useIsAuthenticated } from "../auth/useIsAuthenticated";
import { Icon } from "../common/Icon";
import { CompanySelector } from "../company/CompanySelector";
import { useCompany } from "../company/useCompany";
import { ButtonIcon } from "../components/ButtonIcon";
import { ContentLoading } from "../components/ContentLoading";
import { GenericErrorBoundary } from "../components/GenericError";
import { useBranding, useFeatureFlag } from "../hooks/use-app-metadata";
import { LinkGen } from "../LinkGen";
import { useCanvaMediaSync } from "../media/useCanvaMediaSync";
import { NotificationBell } from "../notifications/NotificationBell";
import { ExpiredConnectionsBanner } from "../settings/ExpiredConnectionsBanner";

export const AppLayout = () => {
	const { logout } = useAuth();
	const [menuOpen, setMenuOpen] = useState(false);
	const [isCompactNav, setIsCompactNav] = useState(false);
	const location = useLocation();
	const isAuthenticated = useIsAuthenticated();
	const company = useCompany();

	const branding = useBranding();
	const showGoogleBusiness = useFeatureFlag("GoogleMyBusinessIntegration");
	const showMetaAds = useFeatureFlag("MetaAdsIntegration");
	const showTikTokAds = useFeatureFlag("TikTokAdsIntegration");
	const showYouTube = useFeatureFlag("YouTubeIntegration");
	const isClientAccount = company.myRole === CompanyUserRole.CLIENT_ACCOUNT;
	const hasFullAccess = !isClientAccount;
	useCanvaMediaSync();

	// biome-ignore lint/correctness/useExhaustiveDependencies: Actually needed because it's a side effect
	useEffect(() => {
		setMenuOpen(false);
	}, [location]);

	useEffect(() => {
		const media = window.matchMedia("(max-width: 991.98px)");
		const sync = () => setIsCompactNav(media.matches);
		sync();
		media.addEventListener("change", sync);
		return () => media.removeEventListener("change", sync);
	}, []);

	useEffect(() => {
		if (!menuOpen) {
			return;
		}

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setMenuOpen(false);
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.body.style.overflow = previousOverflow;
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [menuOpen]);

	return (
		<>
			<a className="layout-app-skip" href="#main-content">
				Skip to content
			</a>
			<header className="layout-app-topbar">
				<ButtonIcon
					icon={menuOpen ? "close" : "menu"}
					aria-label={menuOpen ? "Close navigation" : "Open navigation"}
					aria-expanded={menuOpen}
					aria-controls="app-navigation"
					onClick={() => setMenuOpen((open) => !open)}
				/>
				<img
					src={branding.logoUrl ?? defaultLogo}
					alt=""
					className="layout-app-topbar-logo"
				/>
				{isAuthenticated && isCompactNav && <NotificationBell />}
			</header>
			{menuOpen && (
				<button
					type="button"
					className="layout-app-backdrop"
					aria-label="Close navigation"
					onClick={() => setMenuOpen(false)}
				/>
			)}
			<nav
				id="app-navigation"
				aria-label="Main"
				className={cn("layout-app-menu", { open: menuOpen })}
			>
				<div className="layout-app-menu-top">
					<section className="layout-app-menu-logo">
						<img
							src={branding.logoUrl ?? defaultLogo}
							alt={`${branding.appName} logo`}
						/>
						{isAuthenticated && !isCompactNav && (
							<div className="layout-app-menu-logo-actions">
								<NotificationBell />
							</div>
						)}
					</section>

					{isAuthenticated && (
						<div className="layout-app-menu-company">
							<ul>
								<CompanySelector />
							</ul>
						</div>
					)}

					<p className="layout-app-menu-section">Workspace</p>
					<ul className="layout-app-menu-nav">
						{hasFullAccess && (
							<li>
								<NavLink to={LinkGen.Dashboard()}>
									<span>
										<Icon size={20} name="trending_up" />
										Dashboard
									</span>
								</NavLink>
							</li>
						)}
						<li>
							<NavLink to={LinkGen.Calendar()}>
								<span>
									<Icon size={20} name="calendar_clock" />
									Calendar
								</span>
							</NavLink>
						</li>
						{showYouTube && (
							<li>
								<NavLink to={LinkGen.ShortsQueue()}>
									<span>
										<Icon size={20} name="videocam" />
										Shorts queue
									</span>
								</NavLink>
							</li>
						)}
						{hasFullAccess && (
							<li>
								<NavLink to={LinkGen.Inbox()}>
									<span>
										<Icon size={20} name="inbox" />
										Inbox
									</span>
								</NavLink>
							</li>
						)}
						{hasFullAccess && (
							<li>
								<NavLink to={LinkGen.Media()}>
									<span>
										<Icon size={20} name="browse" />
										Media library
									</span>
								</NavLink>
							</li>
						)}
						<li>
							<NavLink to={LinkGen.Report()}>
								<span>
									<Icon size={20} name="planner_review" />
									Reporting
								</span>
							</NavLink>
						</li>
					</ul>

					{hasFullAccess && (
						<>
							<p className="layout-app-menu-section">Growth</p>
							<ul className="layout-app-menu-nav">
								<li>
									<NavLink to={LinkGen.SeoAudit()}>
										<span>
											<Icon size={20} name="summarize" />
											AI SEO Audit
										</span>
									</NavLink>
								</li>
								<li>
									<NavLink to={LinkGen.Seo()}>
										<span>
											<Icon size={20} name="key" />
											SEO
										</span>
									</NavLink>
								</li>
								{showGoogleBusiness && (
									<li>
										<NavLink to={LinkGen.Google.MyBusiness.profile()}>
											<span>
												<Icon name="store" size={20} />
												Business manager
											</span>
										</NavLink>
									</li>
								)}
								{showMetaAds && (
									<li>
										<NavLink to={LinkGen.MetaAds.Index()}>
											<span>
												<Icon name="campaign" size={20} />
												Meta Ads
											</span>
										</NavLink>
									</li>
								)}
								{showTikTokAds && (
									<li>
										<NavLink to={LinkGen.TikTokAds.Index()}>
											<span>
												<Icon name="music_note" size={20} />
												TikTok Ads
											</span>
										</NavLink>
									</li>
								)}
							</ul>
						</>
					)}
				</div>

				<div className="layout-app-menu-account">
					<p className="layout-app-menu-section">Account</p>
					<ul className="layout-app-menu-nav">
						{hasFullAccess && (
							<li>
								<NavLink to={LinkGen.Settings.Apps()}>
									<span>
										<Icon size={20} name="settings" />
										Settings
									</span>
								</NavLink>
							</li>
						)}
						<li>
							<button type="button" onClick={() => logout.mutate()}>
								<span>
									<Icon size={20} name="logout" />
									Logout
								</span>
							</button>
						</li>
					</ul>
				</div>
			</nav>
			<div className="layout-app-content" id="main-content">
				<Suspense fallback={null}>
					<ExpiredConnectionsBanner />
				</Suspense>
				<GenericErrorBoundary>
					<Suspense
						key={location.key}
						fallback={<ContentLoading className="layout-app-loading" />}
					>
						<Outlet />
					</Suspense>
				</GenericErrorBoundary>
			</div>
		</>
	);
};

interface PageProps extends ComponentPropsWithRef<"section"> {
	title: string;
	subtitle?: ReactNode;
	headerActions?: React.ReactNode;
}

export const AppPage: FC<PageProps> = (props) => {
	const { title, subtitle, headerActions, className, ...rest } = props;
	return (
		<>
			<title>{title}</title>
			<div className="layout-app-page">
				<header className="layout-app-page-header">
					<div className="layout-app-page-heading">
						<h1 className="layout-app-page-title">{title}</h1>
						{subtitle && <p className="layout-app-page-subtitle">{subtitle}</p>}
					</div>
					{headerActions && (
						<div className="layout-app-page-actions">{headerActions}</div>
					)}
				</header>
				<section
					className={cn("layout-app-page-content", className)}
					{...rest}
				/>
			</div>
		</>
	);
};
